import { useState } from "react";
import { Flex, Text, Button, TextField, Spinner } from "@radix-ui/themes";
import { Dialog } from "@/components/shared/dialog";
import Select from "@/components/shared/select/Select";
import { SimulatorChannel } from "@/api/simulator/simulator.model";
import { AiFolderIcon } from "@/v2/assets/icons/AiFolderIcon";
import { Ticket03Icon } from "@/v2/assets/icons/Ticket03Icon";
import { useFlag } from "@/v2/components/feature-flag/FeatureFlagContext";
import { aiAgentNames } from "../simulator/context/release";
import { ReleaseResourceAccessStatus } from "../simulator/types";
import dialogImage from "./assets/dialog_image.png";
import styles from "./CreateAIAgentDialog.module.scss";
import { File02Icon } from "@/v2/assets/icons/File02Icon";

interface CreateAIAgentDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channelType: SimulatorChannel | null;
  onSubmit: (name: string, resourceAccess: Record<string, ReleaseResourceAccessStatus>) => Promise<void>;
  isCreating: boolean;
}

const ACCESS_OPTIONS = [
  { label: "Full Access", value: ReleaseResourceAccessStatus.FULL_ACCESS },
  { label: "No Access", value: ReleaseResourceAccessStatus.NO_ACCESS },
];

const CHANNEL_SUBTITLES: Record<SimulatorChannel, string> = {
  autonomous_ticket_event: "Create your ticket handling agent",
  autonomous_quackchat: "Create your live chat agent",
  copilot: "Create your copilot assistant",
  slackbot: "Create your slackbot agent",
  knowledge_hub: "Create your knowledge hub agent",
  helpcenter: "Create your help center agent",
};

const RESOURCES = [
  {
    key: "snippets",
    label: "Snippets",
    icon: <AiFolderIcon fill="amber" size={18} />,
  },
  {
    key: "articles",
    label: "Articles",
    icon: <File02Icon fill="sky" size={18} />,
  },
  {
    key: "wiki",
    label: "Wiki",
    icon: <File02Icon fill="violet" size={18} />,
  },
  {
    key: "ticketingData",
    label: "Ticketing Data",
    icon: <Ticket03Icon fill="blue" size={18} />,
  },
] as const;

type ResourceKey = (typeof RESOURCES)[number]["key"];

function getRandomName(): string {
  return aiAgentNames[Math.floor(Math.random() * aiAgentNames.length)];
}

export function CreateAIAgentDialog({
  open,
  onOpenChange,
  channelType,
  onSubmit,
  isCreating,
}: CreateAIAgentDialogProps) {
  const [name, setName] = useState(getRandomName);
  const [resourceAccess, setResourceAccess] = useState<Record<ResourceKey, ReleaseResourceAccessStatus>>({
    snippets: ReleaseResourceAccessStatus.FULL_ACCESS,
    articles: ReleaseResourceAccessStatus.FULL_ACCESS,
    wiki: ReleaseResourceAccessStatus.FULL_ACCESS,
    ticketingData: ReleaseResourceAccessStatus.FULL_ACCESS,
  });

  const handleSubmit = async () => {
    if (!name.trim()) return;
    await onSubmit(name.trim(), resourceAccess);
  };

  const handleResourceAccessChange = (key: ResourceKey, value: string) => {
    setResourceAccess((prev) => ({
      ...prev,
      [key]: value as ReleaseResourceAccessStatus,
    }));
  };

  const subtitle = channelType ? CHANNEL_SUBTITLES[channelType] : "Create your AI agent";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <Dialog.Content size="large" className={styles.dialogContent}>
        <div className={styles.formSection}>
          <div className={styles.header}>
            <Text size="6" weight="bold">
              Create your AI agent
            </Text>
            <Text size="2" color="gray">
              {subtitle}
            </Text>
          </div>

          <div className={styles.formContent}>
            <label className={styles.inputLabel}>
              <Text size="2" weight="medium">
                AI Agent Name
              </Text>
              <TextField.Root
                placeholder="Enter agent name"
                value={name}
                onChange={(e) => setName(e.target.value)}
              />
            </label>

            <div className={styles.resourceAccessSection}>
              <div className={styles.resourceAccessHeader}>
                <Text size="2" weight="medium">
                  Resource Access
                </Text>
                <Text size="1" color="gray">
                  Choose how your AI will use each resource when it starts.
                </Text>
              </div>

              <div className={styles.resourceList}>
                {RESOURCES.map((resource) => (
                  <div key={resource.key} className={styles.resourceRow}>
                    <div className={styles.resourceInfo}>
                      <div className={`${styles.resourceIcon} ${styles[resource.key]}`}>
                        {resource.icon}
                      </div>
                      <Text size="2" weight="medium">
                        {resource.label}
                      </Text>
                    </div>
                    <div className={styles.resourceSelect}>
                      <Select
                        options={ACCESS_OPTIONS}
                        value={resourceAccess[resource.key]}
                        onChange={(value) => handleResourceAccessChange(resource.key, value)}
                        size="small"
                        disabled={resource.key === "ticketingData"}
                      />
                    </div>
                  </div>
                ))}
              </div>

              <Text size="1" color="gray">
                With No Access, you'll start with no items selected and can configure access for each item individually in resource settings.
              </Text>
            </div>
          </div>

          <div className={styles.footer}>
            <Button
              size="3"
              variant="solid"
              color="gray"
              highContrast
              onClick={handleSubmit}
              disabled={isCreating || !name.trim()}
              style={{ width: "100%" }}
            >
              {isCreating ? (
                <Flex gap="2" align="center">
                  <Spinner size="1" />
                  Creating...
                </Flex>
              ) : (
                "Create Agent"
              )}
            </Button>
          </div>
        </div>

        <div className={styles.imageSection}>
          <img src={dialogImage} alt="" className={styles.dialogImage} />
        </div>
      </Dialog.Content>
    </Dialog>
  );
}
